/* eslint-disable react/no-array-index-key */
import styled, { css } from 'styled-components';
import { BorderRadiusMedium, Spacing16, Spacing24, Spacing48 } from 'autogen/design-tokens/tokens';
import type { ExtendedPlaceholderShopByItemModel, ExtendedPlaceholderShopByModel } from 'autogen/swagger/all';
import { Slide } from 'components/emblaCarousel';
import ScrollSnapSlider, { ScrollSnapSliderContext, useScrollSnapSlider } from 'components/generic/ScrollSnapSlider';
import NavigationFadeButton from 'components/generic/ScrollSnapSlider/components/NavigationFadeButton';
import { Headline } from 'components/generic/typography';
import Icon from 'components/Icon';
import Shortcut from 'components/Shortcut';
import { invisibleScrollbar } from 'modules/helpers/mixins';
import { breakpoint, sizes } from 'variables';

const gap = Spacing24;
const sliderPadding = Spacing16;

// last number is amount of visible items, plus ~1/3
const widthFormulaXS = `calc(((100vw - (${gap} * 3)) - (${sliderPadding}) * 2) / 3.3)`;
const widthFormulaSM = `calc(((100vw - (${gap} * 4)) - (${sliderPadding}) * 2) / 4.3)`;
const widthFormulaMD = `calc(((100vw - (${gap} * 6)) - (${sliderPadding}) * 2) / 6.3)`;
const widthFormulaLG = `calc(((100vw - (${gap} * 8)) - (${sliderPadding}) * 2) / 8.3)`;

const Wrapper = styled.div<{ $transparentBackground: boolean }>`
    position: relative;
`;

const Header = styled(Headline)`
    margin-bottom: ${Spacing16};

    ${breakpoint.up(sizes.md)} {
        padding: 0;
        margin-bottom: ${Spacing24};
    }
`;

const StyledSlide = styled(Slide)`
    width: ${widthFormulaXS};
    display: flex;
    justify-content: center;

    ${breakpoint.up(sizes.sm)} {
        width: ${widthFormulaSM};
    }

    ${breakpoint.up(sizes.md)} {
        width: ${widthFormulaMD};
    }

    ${breakpoint.up(sizes.lg)} {
        width: ${widthFormulaLG};
    }

    ${breakpoint.up(sizes.xl)} {
        width: 112px;
    }

    ${breakpoint.up(sizes.xxl)} {
        width: 116px;
    }
`;

const StyledScrollSnapSlider = styled(ScrollSnapSlider)<{ itemCount: number }>`
    z-index: -1;
    
    && {
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: auto;
        gap: ${gap};
        padding: 0 ${sliderPadding};
        scroll-padding-inline-end: ${Spacing16};
        scroll-padding-inline-start: ${Spacing16};
        scroll-snap-type: none;
        scroll-snap-stop: normal;
        grid-template-columns: repeat(auto-fit, ${widthFormulaXS});

        ${breakpoint.up(sizes.sm)} {
            grid-template-columns: repeat(auto-fit, ${widthFormulaSM});
        }

        ${breakpoint.up(sizes.md)} {
            grid-template-columns: repeat(auto-fit, ${widthFormulaMD});
        }

        ${breakpoint.up(sizes.lg)} {
            grid-template-columns: repeat(auto-fit, ${widthFormulaLG});
        }

        ${breakpoint.up(sizes.xl)} {
            padding: 0;
            scroll-padding-inline-end: 0;
            scroll-padding-inline-start: 0;
            scroll-snap-type: x mandatory;
            scroll-snap-stop: always;
            grid-template-columns: repeat(auto-fit, 112px);
        }

        ${breakpoint.up(sizes.xxl)} {
            grid-template-columns: repeat(auto-fit, 116px);
        }
    }

    ${invisibleScrollbar()}
`;

const Container = styled.div<{ itemCount: number }>`
    position: relative;
    display: flex;
    margin: 0px -${Spacing16};
    z-index: 0;
`;

const navButtonStyling = css`
    display: none;

    ${breakpoint.up(sizes.xl)} {
        display: block;
        position: absolute;
        top: ${Spacing48};
        padding: ${Spacing16};
        border-radius: ${BorderRadiusMedium};
        transform: translateY(-50%);

        & svg {
            height: ${Spacing16};
            width: ${Spacing16};
        }
    }
`;

const StyledNavigationButtonLeft = styled(NavigationFadeButton)`
    ${navButtonStyling}

    ${breakpoint.up(sizes.xl)} {
        left: 0;
    }
`;

const StyledNavigationButtonRight = styled(NavigationFadeButton)`
    ${navButtonStyling}

    ${breakpoint.up(sizes.xl)} {
        right: 0;
    }
`;

const options = {
    draggable: true,
    loop: false
};

interface IShopByRowProps {
    model: ExtendedPlaceholderShopByModel;
    transparentBackground: boolean;
}

function ShopByItem({ item, ...restProps }: { item: ExtendedPlaceholderShopByItemModel }) {
    const { pageAction, image, text, altText } = item;

    if (!pageAction || !image || !text) {
        return null;
    }

    return (
        <StyledSlide data-datalayer={JSON.stringify(item.dataLayer)} {...restProps}>
            <Shortcut action={pageAction} imageProps={{ src: image, alt: altText || '' }} label={text} />
        </StyledSlide>
    );
}

export default function ShopByRow(props: IShopByRowProps): JSX.Element {
    const {
        model: { items, heading },
        transparentBackground
    } = props;

    const { ref, slider } = useScrollSnapSlider(options);

    return (
        <ScrollSnapSliderContext.Provider value={slider}>
            <Wrapper $transparentBackground={transparentBackground}>
                {heading && <Header size="L">{heading}</Header>}
                <Container itemCount={items.length}>
                    <StyledNavigationButtonLeft direction="prev" slideAmount={7} variant="ghost" size="L" withBorder />
                    <StyledNavigationButtonRight direction="next" slideAmount={7} variant="ghost" size="L" withBorder />
                    <StyledScrollSnapSlider ref={ref} itemCount={items.length}>
                        {items.map((item, index) => (
                            <ShopByItem item={item} key={index} data-slide-index={index} />
                        ))}
                    </StyledScrollSnapSlider>
                </Container>
            </Wrapper>
        </ScrollSnapSliderContext.Provider>
    );
}
